import { Accessor, For, Match, Setter, Show, Signal, Switch, createContext, createEffect, createSignal, on, onCleanup, onMount, useContext } from "solid-js"
import { dataSourceEditor as api } from 'st-app/api'
import { SchemaNode } from "st-app/api"
import styles from './st-data-source-editor.module.css'
import ButtonGroup from "../st-button-group/st-button-group"
import STButton from "../st-button/st-button"
import { showModal } from '../st-modal/st-modal'
import ExtractDataEditor from '../extract-data-editor/extract-data-editor'
import { toast } from "common"

export type DataSourceType = 'SQL Query' | 'ST Query' | 'Extract Data'

const registerLegacyActions = () => {
    try {
        const $ = (window as any).$ as any;
        const top = (window as any).top as any;
        if (!$ || !top || !top.Namespace)
            return;

        $.extend(top.Namespace('ST.Actions.ReportBrowser'), {
            SetSTQueryText: function (r: any) {
                const event = new CustomEvent('reportbrowser:stqueryready', { detail: { query: r.STQueryText['#cdata'] } })
                window.dispatchEvent(event)
            }
        });
    } catch (e) {
        console.error('Error registering legacy actions for STDataSourceEditor', e)
    }
}

registerLegacyActions()

const registerDataSourceEditListener = () => {
    window.addEventListener('datasource:edit', ((e: CustomEvent<{ reportGu: string }>) => {
        const dispose = showModal(
            () => {
                const onSaved = () => { dispose(); };
                window.addEventListener('datasource:saved', onSaved, { once: true });
                onCleanup(() => window.removeEventListener('datasource:saved', onSaved));
                return <STDataSourceEditor reportGu={e.detail.reportGu} />;
            },
            { title: 'Edit Data Source' }
        );
    }) as EventListener);
};

registerDataSourceEditListener();

export class STDataSourceContext {
    type: Accessor<DataSourceType>; setType: Setter<DataSourceType>
    query: Accessor<string>; setQuery: Setter<string>
    executionTime: Accessor<number | undefined>; setExecutionTime: Setter<number | undefined>
    schema: Accessor<SchemaNode | undefined>; setSchema: Setter<SchemaNode | undefined>
    queryError: Accessor<string>; setQueryError: Setter<string>
    loading: Accessor<boolean>; setLoading: Setter<boolean>
    saving: Accessor<boolean>; setSaving: Setter<boolean>
    reportGu: string | undefined

    private queries: Record<string, string> = {}

    constructor(type: Signal<DataSourceType>, query: Signal<string>, reportGu?: string) {
        const [getType, baseSetType] = type;
        const [getQuery, baseSetQuery] = query;

        this.queries[getType()] = getQuery();

        this.type = getType;
        this.query = getQuery;
        this.reportGu = reportGu;

        this.setQuery = ((val: string | ((prev: string) => string)) => {
            const result = baseSetQuery(val as any);
            this.queries[getType()] = getQuery();
            return result;
        }) as Setter<string>;

        this.setType = ((val: DataSourceType | ((prev: DataSourceType) => DataSourceType)) => {
            this.queries[getType()] = getQuery();
            const result = baseSetType(val as any);
            baseSetQuery(this.queries[getType()] ?? '');
            return result;
        }) as Setter<DataSourceType>;

        [this.queryError, this.setQueryError] = createSignal('');
        [this.executionTime, this.setExecutionTime] = createSignal<number | undefined>(0);
        [this.schema, this.setSchema] = createSignal<SchemaNode | undefined>(undefined);
        [this.loading, this.setLoading] = createSignal(false);
        [this.saving, this.setSaving] = createSignal(false);
    }
}

const Context = createContext<STDataSourceContext>()

export class STDataSourceEditorProps {
    type?: Signal<DataSourceType>
    query?: Signal<string>
    reportGu?: string
}

export default function STDataSourceEditor(props: STDataSourceEditorProps) {
    const type = props.type ?? createSignal<DataSourceType>('SQL Query')
    const query = props.query ?? createSignal('')

    const context = new STDataSourceContext(type, query, props.reportGu)

    const onQueryReady = (e: CustomEvent<{ query: string | undefined }>) => {
        context.setQuery(e.detail.query || '');
    }

    onMount(() => {
        window.addEventListener('reportbrowser:stqueryready', onQueryReady as EventListener);

        if (props.reportGu) {
            loadDataSource(context, props.reportGu);
        }
    })

    onCleanup(() => {
        window.removeEventListener('reportbrowser:stqueryready', onQueryReady as EventListener);
    })

    const save = async () => {
        if (!context.reportGu) return;
        context.setSaving(true);
        try {
            await api.Save(context.reportGu, context.type(), context.query());
            window.dispatchEvent(new CustomEvent('datasource:saved'));
        } catch (e) {
            context.setQueryError(e instanceof Error ? e.message : 'Save failed');
        } finally {
            context.setSaving(false);
        }
    };

    return (
        <Context.Provider value={context}>
            <div class={styles['st-data-source-editor']}>
                <Show when={!context.loading()} fallback={<div>Loading...</div>}>
                    <Show when={context.type() !== 'Extract Data'}
                        fallback={
                            <ExtractDataEditorWrapper
                                reportGu={props.reportGu!}
                            />
                        }
                    >
                        <QueryTypeSelector />
                        <QueryEditor />

                        <Show when={context.queryError()}>
                            <span class={styles['query-error']}>{context.queryError()}</span>
                        </Show>

                        <Show when={context.type() === 'SQL Query'}>
                            <SQLQueryHelpMessage />
                        </Show>

                        <div class={styles['preview-section']}>
                            <h3 class={styles['preview-heading']}>Preview Results</h3>
                            <div class={styles['preview-table-container']}>
                                <table class={styles['preview-table']}>
                                    <thead>
                                        <tr>
                                            <th>Column Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <For each={context.schema()?.nodes ?? []}>{(col) => (
                                            <tr>
                                                <td>{col.name}</td>
                                            </tr>
                                        )}</For>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <Show when={context.reportGu}>
                            <div class={styles['save-actions']}>
                                <STButton type="primary" loading={context.saving()} onClick={save}>Save</STButton>
                            </div>
                        </Show>
                    </Show>
                </Show>
            </div>
        </Context.Provider>
    )
}

STDataSourceEditor.defaultProps = new STDataSourceEditorProps()

async function loadDataSource(context: STDataSourceContext, reportGu: string) {
    context.setLoading(true);
    try {
        const info = await api.GetDataSourceInfo(reportGu);
        context.setType((info.queryType ?? 'SQL Query') as DataSourceType);
        context.setQuery(info.query ?? '');
    } catch (e) {
        context.setQueryError(e instanceof Error ? e.message : 'Failed to load data source');
    } finally {
        context.setLoading(false);
    }
}

const ExtractDataEditorWrapper = (props: { reportGu: string }) => {
    const onSaved = () => {
        window.dispatchEvent(new CustomEvent('datasource:saved'));
    };

    return <ExtractDataEditor reportGu={props.reportGu} events={{ onSaved }} />;
}

const SQLQueryHelpMessage = () => {
    return (
        <p class={styles['sql-help-message']}>
            SQL Statements with UPDATE, INSERT, or DELETE are not allowed.
            <br/>
            Focus parameters include{' '}
            <b>@GRADINGPERIODID</b>, <b>@ENROLLMENTPERIODID</b>, <b>@CLASSID</b>, <b>@SCHOOLYEARID</b>, <b>@SCHOOLID</b>,{' '}
            <b>@TEACHERID</b>, <b>@ORGANIZATION_YEAR_GU</b>, <b>@ORGANIZATION_GU</b>, <b>@FOCUS_ORGANIZATION_GU</b>,{' '}
            <b>@FOCUS_ORG_YEAR_GU_LIST</b>, <b>@YEAR_GU</b>, <b>@FOCUS_YEAR_GU</b>, <b>@FOCUS_SCHOOL_YEAR</b>,{' '}
            <b>@FOCUS_STUDENT_ACTIVE_STATE</b>(Active Only = 0, Inactive Only = 1, Active And Inactive = 2){' '}
            AND <b>@USER_GU</b>.
        </p>
    )
}

const QueryTypeSelector = () => {
    const ctx = useDataSourceEditor()

    return (
        <ButtonGroup>
            <STButton type="primary" active={ctx.type() === 'SQL Query'} onClick={() => ctx.setType('SQL Query')}>SQL Query</STButton>
            <STButton type="primary" active={ctx.type() === 'ST Query'} onClick={() => ctx.setType('ST Query')}>Synergy Query</STButton>
        </ButtonGroup>
    )
}

const QueryEditor = () => {
    const ctx = useDataSourceEditor()

    const openQueryDesigner = () => {
        const st = (window as any).top?.ST;
        if (st?.OpenViewInDialog) {
            st.OpenViewInDialog('50AA41AC-0156-4A21-820B-9E0C5A319D43', null, {
                'REV_RENDER': {
                    'SERVER_STATE': {
                        'D': [
                            { '@K': 'REPORT_BROWSER_QUERY_TEXT', '@V': ctx.query() },
                            { '@K': 'FROM_REPORT_BROWSER', '@V': 'Y' }
                        ]
                    }
                }
            });
        } else {
            if (import.meta.env.DEV) {
                setTimeout(() => {
                    const event = new CustomEvent('reportbrowser:stqueryready', { detail: { query: 'K12.Student R0 COLS R0.SisNumber' } })
                    window.dispatchEvent(event)
                }, 500)
            }
        }
    }

    const [validating, setValidating] = createSignal(false)

    const validateQuery = async () => {
        const q = ctx.query()
        if (!q) return

        setValidating(true)
        try {
            const result = await api.ValidateSynergyQuery(q)
            if (result.isValid && result.formattedQuery) {
                ctx.setQuery(result.formattedQuery)
                ctx.setQueryError('')

                toast.notify('success', 'Query is valid.')
            } else {
                ctx.setQueryError('Query is not valid.')
            }
        } catch (e) {
            ctx.setQueryError(e instanceof Error ? e.message : 'Validation failed.')
        } finally {
            setValidating(false)
        }
    }

    createEffect(on(() => [ctx.query()], async () => {
        const q = ctx.query()

        if (!q) {
            ctx.setExecutionTime(undefined)
            ctx.setSchema(undefined)
            ctx.setQueryError('')
        } else {
            try {
                const result = await api.PreviewDataSource(q, ctx.type())
                ctx.setSchema(result)
                ctx.setQueryError('')
            } catch(e) {
                if (e instanceof Error) {
                    ctx.setQueryError(e.message)
                } else {
                    ctx.setQueryError('An error occurred while executing the query.')
                }
            }
        }
    }))

    return (
        <div class={styles['query-editor']}>
            <Switch>
                <Match when={ctx.type() === 'SQL Query'}>
                    <div class={styles['query-header']}>
                        <b>Query</b>
                        <Show when={ctx.executionTime()}>
                            <span>(Last Execution Time: {ctx.executionTime()} seconds)</span>
                        </Show>
                    </div>
                    <textarea class={styles['sql-query-textarea']} value={ctx.query()} onChange={(e) => ctx.setQuery(e.target.value)} />
                </Match>
                <Match when={ctx.type() === 'ST Query'}>
                    <div class={styles['query-header']}>
                        <b>Query</b>
                        <div class={styles['query-actions']}>
                            <STButton onClick={openQueryDesigner}>Query Designer</STButton>
                            <STButton loading={validating()} onClick={validateQuery}>Validate</STButton>
                        </div>
                    </div>
                    <textarea class={styles['sql-query-textarea']} value={ctx.query()} onChange={(e) => ctx.setQuery(e.target.value)} />
                </Match>
            </Switch>
        </div>
    )
}

export const useDataSourceEditor = () => {
    const ctx = useContext(Context)
    if (!ctx) {
        throw new Error('useDataSourceEditor must be used within a STDataSourceEditor')
    }

    return ctx
}
